import { CommandDurations } from './CommandConfig';
import BattleStateManager from '../game/BattleStateManager.js';

// Debug flag for chat/AI logs (enable with ?debugChat=1 or REACT_APP_DEBUG_CHAT=true)
let DEBUG_CHAT = true;
try {
  const qs = new URLSearchParams(window.location.search);
  // eslint-disable-next-line no-undef
  DEBUG_CHAT = (process.env.REACT_APP_DEBUG_CHAT === 'true') || (qs.get('debugChat') === '1') || (localStorage.getItem('debugChat') === '1');
} catch (_) {}

export const chatDbg = (...args) => { try { if (DEBUG_CHAT) console.log(...args); } catch (_) {} };
export const chatWarn = (...args) => { try { if (DEBUG_CHAT) console.warn(...args); } catch (_) {} };

// Test helper: schedule random players joining the JoinScene
function scheduleTestJoins(scene, total = 30) {
  // Resolve JoinScene instance
  const joinScene = scene?.scene?.get?.('JoinScene') || (scene?.scene?.key === 'JoinScene' ? scene : null);
  if (!joinScene || !joinScene.scene?.isActive?.()) {
    chatWarn('[ChatCmd] scheduleTestJoins: JoinScene not available/active');
    return;
  }

  // Prevent multiple concurrent schedules
  if (joinScene._isSchedulingTestJoins) {
    chatWarn('[ChatCmd] Test joins already running');
    return;
  }
  joinScene._isSchedulingTestJoins = true;
  joinScene._scheduledTestTimers = joinScene._scheduledTestTimers || [];

  const sampleSprites = [
    'https://soulwars.s3.us-east-1.amazonaws.com/themes/soulwars-default/players/easy1.png',
    'https://soulwars.s3.us-east-1.amazonaws.com/themes/soulwars-default/players/easy2.png',
    'https://soulwars.s3.us-east-1.amazonaws.com/themes/soulwars-default/players/easy3.png',
    'https://soulwars.s3.us-east-1.amazonaws.com/themes/soulwars-default/players/easy4.png',
    'https://soulwars.s3.us-east-1.amazonaws.com/themes/soulwars-default/players/easy5.png',
    'https://soulwars.s3.us-east-1.amazonaws.com/themes/soulwars-default/players/easy6.png'
  ];
  const sampleNames = ['Nimbleyak','Stormblade','Shadowfox','Ironcrest','Bluemist','Sunfang','Nightwolf','Bronzeleaf','Swiftwing','Ashbreaker'];

  let created = 0;
  const addOne = () => {
    // If the scene was destroyed, stop
    if (!joinScene.scene || joinScene.sys?.isDestroyed) {
      joinScene._isSchedulingTestJoins = false;
      return;
    }

    const idx = created;
    const playerData = {
      username: sampleNames[idx % sampleNames.length] + (Math.floor(idx / sampleNames.length) + 1),
      walletAddress: `test_wallet_${idx}`,
      platform: 'test',
      userId: null,
      heroId: null,
      heroName: sampleNames[idx % sampleNames.length],
      heroLevel: Math.floor(Math.random() * 10) + 1,
      sprite: sampleSprites[idx % sampleSprites.length],
      credits: 0,
      health: 100,
      attack: 50,
      defense: 30,
      speed: 40,
      quickness: 10,
      coordination: 10,
      strength: 10,
      endurance: 10,
      focus: 10,
      self: 10,
      mana: 100,
      stamina: 100,
      inventory: null
    };

    const added = joinScene.addPlayer(playerData);
    if (added) {
      created++;
    } else {
      chatWarn('[ChatCmd] Unable to add test player - stopping schedule');
      joinScene._isSchedulingTestJoins = false;
      return;
    }

    if (created < total) {
      // Schedule next one
      const timer = joinScene.time.delayedCall(Math.random() * 1000 + 500, addOne);
      joinScene._scheduledTestTimers.push(timer);
    } else {
      // All done
      joinScene._isSchedulingTestJoins = false;
      chatWarn(`[ChatCmd] Test joins complete: ${created} players added`);
    }
  };

  // Start with first player
  addOne();
}

export async function handleChatMessage(scene, message, meta = {}) {
  const time = new Date().toLocaleTimeString();
  const fullMessage = meta.fullMessage || {};
  const user = meta.user || {};
  const userId = meta.userId || message.userId || user.id || null;
  const wallet = user.walletAddress || message.walletAddress || message.wallet || message.address || 'unknown';
  //const userId = message.userId || user.id || null;
  
  //if message is empty, return
  /*console.log('[ChatCmd] message', message);
  console.log('[ChatCmd] meta', meta);
  console.log('[ChatCmd] user', user);
  console.log('[ChatCmd] userId', userId);
  console.log('[ChatCmd] wallet', wallet);
  console.log('[ChatCmd] fullMessage', fullMessage);
  console.log('[ChatCmd] time', time);*/
  if (!message ) return;

  // Skip command processing for system messages (server responses)
  // System messages should just be displayed as chat messages
  const username = meta.user || fullMessage.username || message.username || '';
  if (username === 'System' || username === 'system') {
    console.log('[ChatCmd] Skipping command processing for system message:', message);
    return; // Don't process system messages as commands
  }

  // Prefer userId-based targeting when available

  //message could be an object with a message property, or a string
  const messageText = typeof message === 'object' ? message?.message || '' : message || '';
  const rawCommand = (messageText || '').trim();
  const lowerCommand = rawCommand.toLowerCase();
  const parts = lowerCommand.split(/\s+/).filter(Boolean);
  const baseCommand = parts.length > 0 ? parts[0] : '';
  const arg = parts.slice(1).join(' ');
  let player = null;
  if (typeof scene.findPlayerByUserId === 'function' && userId) {
    try { player = scene.findPlayerByUserId(userId) || null; } catch (_) {}
  }
  if (!player && typeof scene.findPlayerByWallet === 'function') {
    try { player = scene.findPlayerByWallet(wallet) || null; } catch (_) {}
  }

  /*console.log('[ChatCmd] handleChatMessage', { cmd, wallet, userId, hasPlayer: !!player, arg });
  console.log('[ChatCmd] message', message);
  console.log('[ChatCmd] meta', meta);
  console.log('[ChatCmd] arg', arg);*/
  console.log('[ChatCmd] processing', { command: baseCommand, wallet, userId, hasPlayer: !!player, arg });

  switch (baseCommand) {
    // Server-side commands (balance, bet, vote, join, play) are now processed on the server
    // before broadcasting. The server sends system messages with results.
    // These cases are left for reference but no longer process the commands client-side.
    case 'bet':
    case 'vote': {
      // Dispatch voting interest event before server processing
      try {
        console.log('🎯 [ChatCmd] Dispatching voting-interest-expressed event');
        window.dispatchEvent(new CustomEvent('voting-interest-expressed'));
        console.log('[ChatCmd] Voting interest expressed', { rawCommand });
        console.log('✅ [ChatCmd] Event dispatched successfully');
      } catch (e) {
        console.warn('[ChatCmd] Failed to dispatch voting interest event:', e);
      }
      
      // Server-side commands - already processed on server
      // Server broadcasts results as system messages via SSE
      // We may receive system messages with results, but no client-side processing needed
      console.log('[ChatCmd] Server-side command received (already processed on server)', { rawCommand });
      break;
    }
    case 'join':
    case 'play': {
      // Server-side commands - already processed on server
      // Server broadcasts results as system messages via SSE
      // We may receive system messages with results, but no client-side processing needed
      console.log('[ChatCmd] Server-side command received (already processed on server)', { rawCommand });
      break;
    }
    case 'show':
      if (player) {
        const focusOptions = { fromShowCommand: true, releaseAfterMs: CommandDurations.highlightMs };
        if (scene.highlightPlayer) {
          scene.highlightPlayer(player, 0xffff00, CommandDurations.highlightMs, focusOptions);
        } else if (scene.focusOnPlayerFromSidebar) {
          scene.focusOnPlayerFromSidebar(player, focusOptions);
        }
      }
      if (scene.showEmote) scene.showEmote(player, '✨');
      console.log('[ChatCmd] show executed', { wallet, player: player?.name });
      break;
    case 'emote':
      if (scene.showEmote) scene.showEmote(player, arg || '🙂');
      console.log('[ChatCmd] emote requested', { wallet, token: arg || '🙂' });
      break;
    case 'love':
    case 'heart':
      if (scene.showEmoteForWallet) scene.showEmoteForWallet(wallet, '❤️');
      else if (scene.showEmote) scene.showEmote(player, '❤️');
      console.log('[ChatCmd] love emote', { wallet });
      break;
    case 'balance':
    case 'bal': { // Shortcut for balance
      // Balance command is handled server-side and sent via socket
      // Server will emit 'balance-updated' event which JoinScene listens for
      console.log('[ChatCmd] Balance command - handled server-side, waiting for socket event');
      break;
    }
    case 'ranked': {
      // Server-side command - already processed on server
      // Server broadcasts results as system messages via SSE
      // Server also emits 'ranked-vote-updated' event which JoinScene listens for
      console.log('[ChatCmd] Ranked vote command - handled server-side, waiting for socket event');
      break;
    }
    case 'buy': {
      // Check if this is a battlefield purchase in PreBattleScene
      if (scene && typeof scene.handleShopBuyCommand === 'function') {
        const result = scene.handleShopBuyCommand({
          userId,
          wallet,
          username: player?.name || meta?.user?.username || 'Player',
          slotNumber: null,
          quantity: null,
          player,
          rawArgs: rawCommand,
          argParts: []
        });
        
        // If PreBattleScene handled it (returned true), we're done
        if (result === true) {
          console.log('[ChatCmd] buy command handled by PreBattleScene');
          break;
        }
      }
      
      // Otherwise, try regular shop buy command
      if (!scene || typeof scene.handleShopBuyCommand !== 'function') {
        console.log('[ChatCmd] buy command ignored - scene missing handler', { wallet, userId });
        break;
      }

      const argParts = arg ? arg.split(/\s+/).filter(Boolean) : [];
      const slotRaw = argParts[0] || '';
      const slotNumber = Number(slotRaw.replace(/[^0-9]/g, ''));
      const quantityRaw = argParts.length > 1 ? argParts[1] : undefined;
      const quantity = quantityRaw != null ? Number(quantityRaw.replace(/[^0-9]/g, '')) : undefined;

      await scene.handleShopBuyCommand({
        userId,
        wallet,
        username: player?.name || meta?.user?.username || 'Player',
        slotNumber,
        quantity,
        player,
        rawArgs: rawCommand,
        argParts
      });
      break;
    }
    case 'skull':
      if (scene.showEmoteForWallet) scene.showEmoteForWallet(wallet, '💀');
      else if (scene.showEmote) scene.showEmote(player, '💀');
      console.log('[ChatCmd] skull emote', { wallet });
      break;
    case 'blood':
      if (scene.showEmoteForWallet) scene.showEmoteForWallet(wallet, '🩸');
      else if (scene.showEmote) scene.showEmote(player, '🩸');
      console.log('[ChatCmd] blood emote', { wallet });
      break;
    case 'h':
    case 'heal': {
      if (player && typeof player.healCommand === 'function') {
        player.healCommand();
      } else if (scene.healPlayer && player && player.health < player.maxHealth) {
        const healAmount = Math.min(250, player.maxHealth - player.health);
        scene.healPlayer(player, healAmount);
        if (scene.showEmote) scene.showEmote(player, '❤️');
        console.log('[ChatCmd] heal fallback invoked', { wallet, player: player?.name, healAmount });
      }
      break;
    }
    case 'beastmode':
    case 'smash':
    case 'hulksmash':
    case 'rage': {
      if (player && typeof player.rageCommand === 'function') {
        const success = player.rageCommand();
        if (!success) {
          console.log('[ChatCmd] rage command unavailable (cooldown or no potion)', { wallet });
        }
        if (success && typeof player.setPrioritizeAttack === 'function') {
          player.setPrioritizeAttack(CommandDurations.attackBiasMs);
        }
      }
      break;
    }
    case 'm':
    case 'mana': {
      if (player && typeof player.manaCommand === 'function') {
        const success = player.manaCommand();
        if (!success && scene?.showEmote) {
          scene.showEmote(player, '❌');
        }
      }
      break;
    }
    case 'surrender':
    case 'quit': {
      if (player && typeof player.setHealthToOne === 'function') {
        player.setHealthToOne();
      } else if (player) {
        player.health = Math.min(player.health, 1);
      }
      break;
    }
    case 'r':
    case 'run':
      if (player && typeof player.runCommand === 'function') {
        const success = player.runCommand();
        if (!success) {
          console.log('[ChatCmd] run on cooldown', { wallet });
        }
      } else if (scene.setPlayerRunAway) {
        scene.setPlayerRunAway(player, CommandDurations.runDurationMs, CommandDurations.runCooldownMs);
        if (scene.showEmote) scene.showEmote(player, '💨');
      }
      break;
    case 'f':
    case 'fire':
    case 'shoot':
    case 'bow':
      if (player && typeof player.shootCommand === 'function') {
        player.shootCommand();
      }
      break;
    case 'a':
    case 'attack':
      if (player && typeof player.attackCommand === 'function') {
        player.attackCommand({ durationMs: CommandDurations.attackBiasMs });
      } else if (scene.setPlayerPrioritizeAttack) {
        scene.setPlayerPrioritizeAttack(player, CommandDurations.attackBiasMs);
        if (scene.showEmote) scene.showEmote(player, '⚔️');
      }
      break;
    case 'l':
    case 'loot':
      if (player && typeof player.lootCommand === 'function') {
        player.lootCommand({ durationMs: CommandDurations.lootBiasMs });
      } else if (scene.showEmote) {
        scene.showEmote(player, '💰');
      }
      break;
    case 'defend':
    case 'd':
      if (player && typeof player.defendCommand === 'function') {
        player.defendCommand(CommandDurations.defendDurationMs);
      }
      break;

    case 'center':
    case 'centre':
      if (player && typeof player.centerCommand === 'function') {
        player.centerCommand();
      }
      break;
    case 's':
    case 'special':
      /*if (scene.setPlayerSpecial) scene.setPlayerSpecial(player, CommandDurations.specialBiasMs);
      if (scene.showEmote) scene.showEmote(player, '💎');
      chatDbg('[ChatCmd] special invoked', { wallet, until: player?.commandState?.specialUntil, lastSpecialAt: player?.commandState?.lastSpecialAt });
      */
      break;
    case 'u':
    case 'ult':
    case 'ultimate':
      if (player && typeof player.useUltimate === 'function') {
        const success = player.useUltimate();
        if (!success && scene?.showEmote) {
          scene.showEmote(player, '⏳');
        }
      }
      break;
    case 'w':
    case 'weapon':
      /*
      if (scene.setPlayerWeapon) scene.setPlayerWeapon(player, CommandDurations.weaponBiasMs);
      if (scene.showEmote) scene.showEmote(player, '🔫');
      chatDbg('[ChatCmd] weapon invoked', { wallet, until: player?.commandState?.weaponUntil, lastWeaponAt: player?.commandState?.lastWeaponAt });
      */
      break;
    case 'testjoins':
      if (user.username === 'dave') {
        const count = parseInt(arg) || 30;
        scheduleTestJoins(scene, count);
        chatWarn(`[ChatCmd] Scheduling ${count} test joins`);
      }
      break;
    case 'lootdelay':
      if (user.username === 'dave' || userId === 1) {
        //set the scene lootDelay to 10000
        if(scene.lootDelay) {
          scene.lootDelay = 10000;
        }
      }
      break;
    case 'firebol':
    case 'meteor storm':
    case 'fireball':
      if (user.username === 'dave' || userId === 1) {
        // Damage all players (admin test command)
        const players = scene.players || [];
        players.forEach(p => {
          if (p && typeof p.takeDamage === 'function') {
            p.takeDamage(600);
          }
        });
      }
      break;
    case 'lootscreen': {
      // Test command to load BattlefieldLootScene with dummy data
      // Only works locally (localhost or when user is dave/userId 1)
      const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (isLocal && userId === 1) {
        console.log('🏴‍☠️ [ChatCmd] Loading BattlefieldLootScene with dummy data');
        
        // Create dummy battlefield loot data
        const dummyData = {
          lootEntries: [
            { id: '1', loot_type: 'token', token_symbol: 'SOULWARS', amount: 5000, owner_kept_amount: 1250 },
            { id: '2', loot_type: 'token', token_symbol: 'SOULWARS', amount: 3000, owner_kept_amount: 750 },
            { id: '3', loot_type: 'item', theme_item_id: 'sword_iron_t1', amount: 15, owner_kept_amount: 3 },
            { id: '4', loot_type: 'item', theme_item_id: 'shield_wood_t1', amount: 8, owner_kept_amount: 2 },
            { id: '5', loot_type: 'potion', theme_item_id: 'potion_health_t1', amount: 25, owner_kept_amount: 6 },
            { id: '6', loot_type: 'potion', theme_item_id: 'potion_mana_t1', amount: 20, owner_kept_amount: 5 },
            { id: '7', loot_type: 'gem', theme_item_id: 'gem_power_t1', amount: 12, owner_kept_amount: 3 }
          ],
          totals: {
            tokens: 8000,
            items: 23,
            potions: 45,
            gems: 12
          },
          ownerKept: {
            tokens: 2000,
            items: 5,
            potions: 11,
            gems: 3
          },
          battlefield: {
            id: 'test-battlefield-123',
            owner_user_id: 1,
            ownerUsername: 'DaveY',
            themeBattlefield: {
              name: 'Danger Marshes',
              theme_id: 'soulwars-default'
            }
          }
        };
        
        // Access the game instance and scene manager
        // Try multiple ways to access the scene manager
        let sceneManager = null;
        let game = null;
        
        // First, try to get the game from the scene
        if (scene && scene.game) {
          game = scene.game;
        } else if (scene && scene.scene && scene.scene.systems && scene.scene.systems.game) {
          game = scene.scene.systems.game;
        }
        
        // If we have a game, get the scene manager
        if (game && game.scene) {
          sceneManager = game.scene;
        } else if (scene && scene.scene && scene.scene.manager) {
          sceneManager = scene.scene;
        }
        
        // Fallback: try window reference
        if (!sceneManager && window.phaserGameRef && window.phaserGameRef.current) {
          game = window.phaserGameRef.current;
          if (game && game.scene) {
            sceneManager = game.scene;
          }
        }
        
        if (sceneManager) {
          try {
            // Start the BattlefieldLootScene with dummy data
            sceneManager.start('BattlefieldLootScene', {
              battleId: 'test-battle-' + Date.now(),
              battlefieldInstanceId: 'test-battlefield-123',
              dummyData: dummyData
            });
            console.log('✅ [ChatCmd] BattlefieldLootScene started with dummy data');
          } catch (error) {
            console.error('❌ [ChatCmd] Error starting BattlefieldLootScene:', error);
            console.error('❌ [ChatCmd] Error details:', error.message, error.stack);
          }
        } else {
          console.warn('⚠️ [ChatCmd] Could not access scene manager. Scene:', scene);
          console.warn('⚠️ [ChatCmd] window.phaserGameRef:', window.phaserGameRef);
        }
      } else {
        console.log('[ChatCmd] loot command only available locally or for admin users');
      }
      break;
    }
    case 'item spotlight screen':
    case 'itemspotlight': {
      // Test command to load ItemSpotlightScene
      // Only works locally (localhost or when user is dave/userId 1)
      const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
      if (isLocal || user.username === 'dave' || userId === 1) {
        console.log('⚔️ [ChatCmd] Loading ItemSpotlightScene');
        
        // Access the game instance and scene manager
        let sceneManager = null;
        let game = null;
        
        // First, try to get the game from the scene
        if (scene && scene.game) {
          game = scene.game;
        } else if (scene && scene.scene && scene.scene.systems && scene.scene.systems.game) {
          game = scene.scene.systems.game;
        }
        
        // If we have a game, get the scene manager
        if (game && game.scene) {
          sceneManager = game.scene;
        } else if (scene && scene.scene && scene.scene.manager) {
          sceneManager = scene.scene;
        }
        
        // Fallback: try window reference
        if (!sceneManager && window.phaserGameRef && window.phaserGameRef.current) {
          game = window.phaserGameRef.current;
          if (game && game.scene) {
            sceneManager = game.scene;
          }
        }
        
        if (sceneManager) {
          try {
            // Start the ItemSpotlightScene
            sceneManager.start('ItemSpotlightScene', {
              itemData: {
                id: 'sword_of_truth',
                name: 'Sword of Truth',
                rarity: 'Rare',
                type: 'melee',
                damage: { min: 8, max: 20 },
                attackSpeed: 1.2,
                price: 250000,
                priceSymbol: 'SOULWARS',
                normalStats: {
                  damage: { min: 8, max: 15 },
                  attackSpeed: 1.0
                }
              }
            });
            console.log('✅ [ChatCmd] ItemSpotlightScene started');
          } catch (error) {
            console.error('❌ [ChatCmd] Error starting ItemSpotlightScene:', error);
            console.error('❌ [ChatCmd] Error details:', error.message, error.stack);
          }
        } else {
          console.warn('⚠️ [ChatCmd] Could not access scene manager. Scene:', scene);
          console.warn('⚠️ [ChatCmd] window.phaserGameRef:', window.phaserGameRef);
        }
      } else {
        console.log('[ChatCmd] item spotlight screen command only available locally or for admin users');
      }
      break;
    }
    case 'showwelcome': {
      // Command to show FirstTimeScene
      console.log('👋 [ChatCmd] Loading FirstTimeScene');
      
      // Access the game instance and scene manager
      let sceneManager = null;
      let game = null;
      
      // First, try to get the game from the scene
      if (scene && scene.game) {
        game = scene.game;
      } else if (scene && scene.scene && scene.scene.systems && scene.scene.systems.game) {
        game = scene.scene.systems.game;
      }
      
      // If we have a game, get the scene manager
      if (game && game.scene) {
        sceneManager = game.scene;
      } else if (scene && scene.scene && scene.scene.manager) {
        sceneManager = scene.scene;
      }
      
      // Fallback: try window reference
      if (!sceneManager && window.phaserGameRef && window.phaserGameRef.current) {
        game = window.phaserGameRef.current;
        if (game && game.scene) {
          sceneManager = game.scene;
        }
      }
      
      if (sceneManager) {
        try {
          // Get battle data from BattleStateManager or use empty defaults
          const battleResults = BattleStateManager.getBattleResults() || {};
          const winners = BattleStateManager.getWinners() || [];
          const allPlayers = BattleStateManager.getPlayerList() || [];
          const battleRewards = BattleStateManager.getBattleRewards() || null;
          
          // Start the FirstTimeScene
          sceneManager.start('FirstTimeScene', {
            battleResults,
            winners,
            allPlayers,
            battleRewards
          });
          console.log('✅ [ChatCmd] FirstTimeScene started');
        } catch (error) {
          console.error('❌ [ChatCmd] Error starting FirstTimeScene:', error);
          console.error('❌ [ChatCmd] Error details:', error.message, error.stack);
        }
      } else {
        console.warn('⚠️ [ChatCmd] Could not access scene manager. Scene:', scene);
        console.warn('⚠️ [ChatCmd] window.phaserGameRef:', window.phaserGameRef);
      }
      break;
    }
    default:
      //chatDbg('[ChatCmd] unknown command', { cmd, raw });
      break;
  }
}